import { Ref } from 'react';

import { Field, FieldProps } from 'react-final-form';

import {
  LabelWithDescription,
  LabelWithDescriptionProps,
} from '@cast/design-system';

import { useControlsGroupState } from './controls-state';
import { useRffFormState } from '../hooks';
import { resolveRenderableError, showErrorOnChange } from '../utils';

export type RffLabelWithDescriptionProps = Omit<
  LabelWithDescriptionProps,
  'error'
> & {
  ref?: Ref<HTMLDivElement>;
  /** Name of the form field whose validation error renders as the error row. */
  name: string;
  fieldProps?: Partial<FieldProps<string, any>>;
};

/**
 * Binds the design-system `LabelWithDescription` to a react-final-form field:
 * the field's validation error (gated by `showErrorOnChange`) renders as the
 * block's error row, and `disabled` is resolved from the form mode and the
 * nearest `RffControlsProvider` state. Presentational only — unlike
 * `RffControlLabel` it does not broadcast `ControlLabelContext`, so a paired
 * control receives no id/error/disabled through it; associate the control via
 * `slotProps.label.htmlFor` instead.
 */
export const RffLabelWithDescription = ({
  ref,
  fieldProps = {},
  name: _name,
  ...labelProps
}: RffLabelWithDescriptionProps) => {
  const { mode } = useRffFormState();
  const { disabled, readOnly, name } = useControlsGroupState({
    disabled: labelProps.disabled,
    readOnly: mode === 'view',
    name: _name,
  });

  return (
    <Field
      {...fieldProps}
      name={name || _name}
      render={({ meta }) => {
        const showError = showErrorOnChange(meta);
        return (
          <LabelWithDescription
            ref={ref}
            {...labelProps}
            error={
              showError
                ? resolveRenderableError(meta.error) ??
                  resolveRenderableError(meta.submitError)
                : undefined
            }
            disabled={disabled || readOnly}
          />
        );
      }}
    />
  );
};

RffLabelWithDescription.displayName = 'RffLabelWithDescription';
